import React, { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import SEO from '../components/SEO';
import ScrollToTop from '../components/ScrollToTop';

const DonationCancel = () => {
  useEffect(() => {
    // Track cancelled donation for analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'donation_cancelled', {
        event_category: 'donations',
        event_label: 'cancel_page'
      });
    }
  }, []);

  return (
    <>
      <SEO 
        title="Donation Cancelled - WeFlare"
        description="Your donation was cancelled. You can try again anytime to support WeFlare's mission."
        keywords="donation cancelled, WeFlare, support"
      />
      <ScrollToTop />
      
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8 text-center">
            {/* Cancel Icon */}
            <div className="mb-4">
              <div className="cancel-icon-container d-inline-flex align-items-center justify-content-center rounded-circle bg-warning text-white mb-4" 
                   style={{width: '120px', height: '120px', fontSize: '3rem'}}>
                <i className="bi bi-x-circle-fill"></i>
              </div>
            </div>

            {/* Cancel Message */}
            <h1 className="text-display mb-4" style={{color: 'var(--gray-800)'}}>
              Donation Cancelled
            </h1>
            
            <p className="lead text-muted mb-4">
              Your donation was cancelled and no payment was processed. Don't worry - you can try again anytime.
            </p>

            {/* Reassurance Message */}
            <div className="card-modern p-4 mb-4">
              <h5 className="text1 mb-3">No Worries!</h5>
              <p className="text-muted mb-3">
                We understand that sometimes you need to reconsider or there might be technical issues. 
                Your support means the world to us, and we're here whenever you're ready to contribute.
              </p>
              <p className="text-muted mb-0">
                <strong>No charges were made to your account.</strong>
              </p>
            </div>

            {/* Alternative Ways to Help */}
            <div className="card-modern p-4 mb-4 text-start">
              <h5 className="text1 mb-3 text-center">Other Ways You Can Help</h5>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="text-center">
                    <i className="bi bi-share fs-2 text-primary mb-2"></i>
                    <h6>Share Our Mission</h6>
                    <small className="text-muted">Help us reach more people by sharing our story on social media.</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center">
                    <i className="bi bi-people fs-2 text-success mb-2"></i>
                    <h6>Volunteer</h6>
                    <small className="text-muted">Join our team of volunteers and contribute your time and skills.</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center">
                    <i className="bi bi-envelope fs-2 text-warning mb-2"></i>
                    <h6>Stay Connected</h6>
                    <small className="text-muted">Subscribe to our newsletter for updates and future opportunities.</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Common Issues and Solutions */}
            <div className="card-modern p-4 mb-4 text-start">
              <h5 className="text1 mb-3 text-center">Having Issues?</h5>
              <div className="accordion" id="helpAccordion">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#help1">
                      Payment method not working?
                    </button>
                  </h2>
                  <div id="help1" className="accordion-collapse collapse" data-bs-parent="#helpAccordion">
                    <div className="accordion-body">
                      Try using a different payment method or check with your bank if there are any restrictions on international transactions.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#help2">
                      Technical difficulties?
                    </button>
                  </h2>
                  <div id="help2" className="accordion-collapse collapse" data-bs-parent="#helpAccordion">
                    <div className="accordion-body">
                      Try refreshing the page, clearing your browser cache, or using a different browser. If issues persist, contact our support team.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#help3">
                      Need to speak with someone?
                    </button>
                  </h2>
                  <div id="help3" className="accordion-collapse collapse" data-bs-parent="#helpAccordion">
                    <div className="accordion-body">
                      Contact us at <a href="mailto:info@weflare.org">info@weflare.org</a> or through our social media channels. We're here to help!
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex flex-wrap gap-3 justify-content-center mb-4">
              <Link to="/donate" className="btn-modern btn-primary">
                <i className="bi bi-heart me-2"></i>
                Try Donating Again
              </Link>
              <Link to="/" className="btn-modern btn-outline">
                <i className="bi bi-house me-2"></i>
                Return Home
              </Link>
              <Link to="/programs/overview" className="btn-modern btn-outline">
                <i className="bi bi-eye me-2"></i>
                Learn About Our Work
              </Link>
            </div>

            {/* Contact Information */}
            <div className="bg-light p-4 rounded-3">
              <h6 className="text-primary mb-3">Need Help?</h6>
              <p className="text-muted mb-2">
                If you're experiencing technical issues or have questions about donating, we're here to help.
              </p>
              <div className="d-flex gap-3 justify-content-center">
                <a href="mailto:info@weflare.org" className="text-decoration-none">
                  <i className="bi bi-envelope me-1"></i>
                  info@weflare.org
                </a>
                <span className="text-muted">|</span>
                <NavLink to="/#contact" className="text-decoration-none"
                  onClick={(e) => {
                    // If we're already on the home page, scroll to contact
                    if (window.location.pathname === '/') {
                      e.preventDefault();
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }}
                >
                  <i className="bi bi-chat-dots me-1"></i>
                  Contact Form
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DonationCancel;